<template>
  <span v-if="unavailable" class="attachment attachment--unavailable" :title="attachment.file_name">
    <FileX :size="12" /> File unavailable
  </span>

  <a
    v-else-if="isImage"
    :href="url"
    target="_blank"
    rel="noopener"
    class="attachment attachment--image"
    :title="attachment.file_name"
  >
    <img :src="url" :alt="attachment.file_name" class="attachment__preview" @error="unavailable = true" />
    <span class="attachment__caption"><Paperclip :size="12" /> {{ attachment.file_name }}</span>
  </a>

  <a v-else :href="url" target="_blank" rel="noopener" class="attachment">
    <Paperclip :size="12" /> {{ attachment.file_name }}
  </a>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { FileX, Paperclip } from "lucide-vue-next"
import { PatientConversationAttachmentDTO } from "@vitable/asclepius-api-client-ts"
import {
  isImageAttachment,
  resolveAttachmentUrl
} from "@/modules/patient-conversations/constants/patient-conversation-attachments"

// Images preview inline and report a broken file themselves; other files open in a tab through their signed URL
const props = defineProps<{
  attachment: PatientConversationAttachmentDTO
}>()

const url = computed<string>(() => resolveAttachmentUrl(props.attachment.download_url))
const isImage = computed<boolean>(() => isImageAttachment(props.attachment))
const unavailable = ref<boolean>(!props.attachment.download_url)
</script>

<style scoped lang="sass">
@import "@/styles/colors"

.attachment
  display: inline-flex
  align-items: center
  gap: 4px
  padding: 3px 8px
  border: 1px solid $border
  border-radius: 6px
  font-size: 12px
  color: $primary
  text-decoration: none

  &:hover
    color: $highlight-dark
    border-color: $primary

  &--unavailable
    color: $grey
    border-style: dashed
    cursor: default

    &:hover
      color: $grey
      border-color: $border

  &--image
    flex-direction: column
    align-items: flex-start
    gap: 4px
    padding: 4px

  &__preview
    display: block
    max-width: 220px
    max-height: 160px
    border-radius: 4px
    object-fit: cover

  &__caption
    display: inline-flex
    align-items: center
    gap: 4px
    padding: 0 4px
</style>
